/**
 * @module PrivateSubgraphs
 *
 * This module handles the GraphQL queries and mutations related to private subgraphs.
 * It defines two hooks, `usePrivateSubgraphsQuery` and `usePrivateSubgraphSyncMutation`,
 * to interact with private subgraphs within an account.
 */

import { gql, useLazyQuery, useMutation } from '@apollo/client';

import {
  CloudProvider,
  PrivateSubgraphSync,
  PrivateSubgraphsQuery,
  SyncPrivateSubgraphsInput,
} from 'src/lib/graphqlTypes/types';

/**
 * GraphQL query to get the private subgraphs for a specific account and cloud provider.
 * @type {DocumentNode}
 */
const GET_PRIVATE_SUBGRAPHS = gql`
  query PrivateSubgraphsQuery($accountId: ID!, $cloudProvider: CloudProvider!) {
    account(id: $accountId) {
      id
      privateSubgraphs(cloudProvider: $cloudProvider) {
        name
        cloudProvider
        region {
          name
          code
          provider
          state
          country
        }
        domainUrl
        status
      }
    }
  }
`;

/**
 * GraphQL mutation to sync private subgraphs for a specific account.
 * @type {DocumentNode}
 */
const PRIVATE_SUBGRAPH_SYNC = gql`
  mutation PrivateSubgraphSync(
    $input: SyncPrivateSubgraphsInput!
    $accountId: ID!
  ) {
    account(id: $accountId) {
      privateSubgraph {
        sync(input: $input) {
          name
          cloudProvider
          region {
            name
            code
            provider
            state
            country
          }
          domainUrl
          status
        }
      }
    }
  }
`;

/**
 * Hook to execute a lazy query for private subgraphs.
 *
 * @param {string} accountId - The ID of the account for which to fetch private subgraphs.
 * @param {string} cloudProvider - The cloud provider of the subgraphs.
 * @returns {Object} - An object containing the following keys:
 *                     - getPrivateSubgraphs: Function to execute the query.
 *                     - data: Data returned from the query.
 *                     - loading: Loading state of the query.
 *                     - error: Errors if the query fails.
 */
export const usePrivateSubgraphsQuery = (
  accountId: string,
  cloudProvider: CloudProvider,
) => {
  const [getPrivateSubgraphs, { data, loading, error }] =
    useLazyQuery<PrivateSubgraphsQuery>(GET_PRIVATE_SUBGRAPHS, {
      variables: {
        accountId,
        cloudProvider,
      },
    });

  return { getPrivateSubgraphs, data, loading, error };
};

/**
 * Hook to execute a mutation for syncing private subgraphs.
 *
 * @returns {Object} - An object containing the following keys:
 *                     - sync: Function to execute the mutation.
 *                     - data: Data returned from the mutation.
 *                     - loading: Loading state of the mutation.
 *                     - error: Errors if the mutation fails.
 */
export const usePrivateSubgraphSyncMutation = () => {
  const [syncPrivateSubgraphs, { data, loading, error }] =
    useMutation<PrivateSubgraphSync>(PRIVATE_SUBGRAPH_SYNC);

  /**
   * Async function to execute the sync mutation.
   *
   * @param {SyncPrivateSubgraphsInput} input - The input object for syncing.
   * @param {string} accountId - The ID of the account for which to sync.
   * @returns {Promise} - Returns a promise resolving to the mutation result.
   */
  const sync = async (input: SyncPrivateSubgraphsInput, accountId: string) => {
    return syncPrivateSubgraphs({ variables: { input, accountId } });
  };

  return { sync, data, loading, error };
};
